/**
 * STABILITY-CRITICAL: sequência que antecede qualquer apagamento de dados locais — gate de
 * pendências, backup e recontagem final. Se qualquer passo falhar, NADA é apagado.
 * Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável pelo sync.
 */
import { BackupAbortedError } from './backupConfirmed'
import type { BackupFile } from './backupConfirmed'
import { blockingUnreadable, runPendingGate } from './pendingGate'
import type { GateDeps, GateReason, GateResult } from './pendingGate'
import { buildBackupWarnings } from './messages'
import type { FlowEvent } from './flowEvents'
import type { PendingSummary } from '../pendingData'

export type BlockedInfo = {
  reason: GateReason
  message: string
  tables?: Record<string, number>
}
export type BackupRequest = {
  file: BackupFile
  canUsePicker: boolean
  mustConfirm: boolean
  warnings: string[]
}
export interface FlowUi {
  setStep(step: 1 | 2 | 3 | 4, message?: string): void
  /** Chamado logo antes da parte irreversível: desliga o beforeunload e mostra "finalizando". */
  finishing(): void
  showBlocked(info: BlockedInfo): Promise<'retry' | 'cancel'>
  requestBackupSave(
    req: BackupRequest,
  ): Promise<{ method: 'picker' | 'download' }>
}

export type SafeguardDeps = {
  gate: GateDeps
  runGate?: (g: GateDeps) => Promise<GateResult>
  getSummary(): Promise<PendingSummary>
  prepareBackup(a: { motivo: 'limpeza' | 'saida' }): Promise<BackupFile>
  silentSave(file: BackupFile): void
  canUsePicker(): boolean
}

type Cleared = Extract<GateResult, { status: 'clear' | 'clear_refused' }>
export type SafeguardFailure =
  | 'blocked_cancelled'
  | 'backup_failed'
  | 'backup_cancelled'
  | 'changed_after_backup'
export type SafeguardOutcome =
  | {
      ok: true
      gate: Cleared
      backup: BackupFile
      savedWith: 'picker' | 'download' | 'silent'
      mustConfirm: boolean
    }
  | { ok: false; reason: SafeguardFailure; message: string; detail?: unknown }

type Emit = (e: Omit<FlowEvent, 'at'>) => void

export async function runSafeguards(a: {
  mode: 'limpeza' | 'saida'
  deps: SafeguardDeps
  ui: FlowUi
  emit: Emit
}): Promise<SafeguardOutcome> {
  const { mode, deps, ui, emit } = a
  const runGate = deps.runGate ?? runPendingGate
  const fail = (
    reason: SafeguardFailure,
    message: string,
    detail?: unknown,
  ): SafeguardOutcome => ({
    ok: false,
    reason,
    message,
    detail,
  })

  // Passo 1 — empurrar e recontar (loop "Tentar de novo" enquanto bloqueado)
  ui.setStep(1, 'Enviando o que ainda não foi enviado ao servidor...')
  let gate: GateResult
  for (;;) {
    gate = await runGate(deps.gate)
    if (gate.status !== 'blocked') break
    emit({
      level: 'warn',
      category: 'sync',
      action: `${mode}_blocked`,
      payload: {
        reason: gate.reason,
        tables: gate.tables ?? null,
        rounds: gate.rounds,
      },
    })
    const choice = await ui.showBlocked({
      reason: gate.reason,
      message: gate.message,
      tables: gate.tables,
    })
    if (choice === 'cancel') return fail('blocked_cancelled', gate.message)
    ui.setStep(1, 'Enviando o que ainda não foi enviado ao servidor...')
  }
  const cleared = gate as Cleared
  const refused = cleared.status === 'clear_refused' ? cleared.refused : null
  const mustConfirm = cleared.dlq.total > 0 || refused !== null

  // Passo 2 — montar o zip ANTES de pedir o clique (decisão 2) e salvar
  ui.setStep(2, 'Gerando o arquivo de backup...')
  let backup: BackupFile
  try {
    backup = await deps.prepareBackup({ motivo: mode })
  } catch (e) {
    emit({
      level: 'error',
      category: 'outro',
      action: `${mode}_backup_failed`,
      payload: { message: String((e as Error)?.message ?? e) },
    })
    return fail('backup_failed', 'Não foi possível gerar o backup.', e)
  }

  let savedWith: 'picker' | 'download' | 'silent'
  if (mode === 'saida' && !mustConfirm) {
    try {
      deps.silentSave(backup)
      savedWith = 'silent'
    } catch (e) {
      return fail('backup_failed', 'Não foi possível salvar o backup.', e)
    }
  } else {
    try {
      const r = await ui.requestBackupSave({
        file: backup,
        canUsePicker: deps.canUsePicker(),
        mustConfirm,
        warnings: buildBackupWarnings(cleared.dlq, refused),
      })
      savedWith = r.method
    } catch (e) {
      if (
        e instanceof BackupAbortedError ||
        (e as Error)?.name === 'BackupAbortedError'
      ) {
        return fail('backup_cancelled', 'O backup não foi salvo.', e)
      }
      return fail('backup_failed', 'Não foi possível salvar o backup.', e)
    }
  }

  // Recontagem final: o zip foi montado antes; se algo novo ficou sujo, ele não está no arquivo.
  let after: PendingSummary
  try {
    after = await deps.getSummary()
  } catch (e) {
    return fail(
      'changed_after_backup',
      'Não foi possível reconferir os dados.',
      e,
    )
  }
  if (
    blockingUnreadable(after).length > 0 ||
    after.dirtyRecords > cleared.summary.dirtyRecords
  ) {
    return fail('changed_after_backup', 'Os dados mudaram depois do backup.')
  }

  emit({
    level: 'info',
    category: 'outro',
    action: `${mode}_backup_saved`,
    payload: {
      method: savedWith,
      sizeKb: backup.sizeKb,
      farmsCount: backup.farmsCount,
      mustConfirm,
    },
  })
  return { ok: true, gate: cleared, backup, savedWith, mustConfirm }
}

/** Payload do evento `dlq_discarded_on_reset` (Dec. 9): só contagens/entidades, nunca dados. */
export function buildDiscardPayload(
  mode: 'limpeza' | 'saida',
  out: { gate: Cleared; savedWith: string },
) {
  const d = out.gate.dlq
  return {
    mode,
    backupMethod: out.savedWith,
    dlq: {
      total: d.total,
      temporary: d.temporary,
      permanent: d.permanent,
      unknown: d.unknown,
      byEntity: d.byEntity,
    },
    refusedDirtyByTable:
      out.gate.status === 'clear_refused' ? out.gate.refused.byTable : null,
    refusedVia:
      out.gate.status === 'clear_refused' ? out.gate.refused.via : null,
  }
}
